import { useMemo } from 'react';

export type HotelUserRole = 'hotel_admin' | 'hotel_manager' | 'hotel_supervisor' | 'hotel_accountant' | 'receptionist' | 'staff' | 'super_admin';

export interface HotelUser {
  id: string;
  email: string;
  name?: string;
  role: HotelUserRole;
  hotelId: string;
  hotelName?: string;
}

export interface HotelPermissionConfig {
  roles: HotelUserRole[];
}

// Définition des permissions pour le portail hôtel
export const HOTEL_PERMISSIONS = {
  // Dashboard admin
  ADMIN_DASHBOARD: { roles: ['hotel_admin', 'hotel_manager', 'super_admin'] as HotelUserRole[] },

  // Gestion du personnel
  STAFF_VIEW: { roles: ['hotel_admin', 'hotel_manager'] as HotelUserRole[] },
  STAFF_CREATE: { roles: ['hotel_admin'] as HotelUserRole[] },
  STAFF_EDIT: { roles: ['hotel_admin'] as HotelUserRole[] },
  STAFF_DELETE: { roles: ['hotel_admin'] as HotelUserRole[] },

  // Paramètres de l'hôtel
  SETTINGS_VIEW: { roles: ['hotel_admin', 'hotel_manager'] as HotelUserRole[] },
  SETTINGS_EDIT: { roles: ['hotel_admin'] as HotelUserRole[] },

  // Gestion des invités
  GUESTS_VIEW: { roles: ['hotel_admin', 'hotel_manager', 'hotel_supervisor', 'hotel_accountant', 'receptionist', 'staff'] as HotelUserRole[] },
  GUESTS_CHECKIN: { roles: ['hotel_admin', 'hotel_manager', 'hotel_supervisor', 'receptionist'] as HotelUserRole[] },
  GUESTS_CHECKOUT: { roles: ['hotel_admin', 'hotel_manager', 'hotel_supervisor', 'receptionist'] as HotelUserRole[] },
  GUESTS_EDIT: { roles: ['hotel_admin', 'hotel_manager', 'hotel_supervisor', 'receptionist'] as HotelUserRole[] },

  // Messages
  MESSAGES_VIEW: { roles: ['hotel_admin', 'hotel_manager', 'hotel_supervisor', 'receptionist'] as HotelUserRole[] },
  MESSAGES_SEND: { roles: ['hotel_admin', 'hotel_manager', 'hotel_supervisor', 'receptionist'] as HotelUserRole[] },

  // Rapports et finances
  REPORTS_VIEW: { roles: ['hotel_admin', 'hotel_manager', 'hotel_accountant'] as HotelUserRole[] },
  REPORTS_EXPORT: { roles: ['hotel_admin', 'hotel_manager', 'hotel_accountant'] as HotelUserRole[] },

  // Alertes de sécurité
  ALERTS_VIEW: { roles: ['hotel_admin', 'hotel_manager', 'hotel_supervisor', 'hotel_accountant', 'receptionist'] as HotelUserRole[] },
  ALERTS_ACKNOWLEDGE: { roles: ['hotel_admin', 'hotel_manager', 'hotel_supervisor', 'receptionist'] as HotelUserRole[] },
} as const;

export type HotelPermission = keyof typeof HOTEL_PERMISSIONS;

/**
 * Hook pour gérer les permissions utilisateur du portail hôtel
 */
export function useHotelPermissions() {
  const user = useMemo((): HotelUser | null => {
    // Try the 'user' key first (full user object)
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr) as HotelUser;
      } catch {
        // ignore parse error
      }
    }

    // Fallback: reconstruct user from individual localStorage keys set by authService.login()
    const token = localStorage.getItem('token');
    const hotelId = localStorage.getItem('hotelId');
    const hotelName = localStorage.getItem('hotelName');
    if (!token || !hotelId) return null;

    // Decode JWT to extract role and email
    let role: HotelUserRole = 'hotel_admin'; // default to admin for hotel portal users
    let email = '';
    let userId = hotelId;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Map backend role values to frontend permission system roles
      const backendRole = (payload.role || '').toString();
      const roleMap: Record<string, HotelUserRole> = {
        'hotel': 'hotel_admin',
        'ADMIN': 'hotel_admin',
        'MANAGER': 'hotel_manager',
        'SUPERVISOR': 'hotel_supervisor',
        'ACCOUNTANT': 'hotel_accountant',
        'RECEPTIONIST': 'receptionist',
        'STAFF': 'staff',
        'hotel_admin': 'hotel_admin',
        'hotel_manager': 'hotel_manager',
        'hotel_supervisor': 'hotel_supervisor',
        'hotel_accountant': 'hotel_accountant',
        'receptionist': 'receptionist',
        'staff': 'staff',
        'SUPER_ADMIN': 'super_admin',
        'super_admin': 'super_admin',
      };
      role = roleMap[backendRole] || 'hotel_admin';
      email = payload.email || payload.sub || '';
      userId = payload.user_id || payload.sub || hotelId;
    } catch {
      // If JWT decode fails, keep defaults
    }

    return {
      id: userId,
      email,
      name: hotelName || undefined,
      role,
      hotelId,
      hotelName: hotelName || undefined,
    };
  }, []);

  const userRole = user?.role || 'hotel_admin';

  /**
   * Vérifie si l'utilisateur a une permission spécifique
   */
  const hasPermission = (permission: HotelPermission): boolean => {
    const config = HOTEL_PERMISSIONS[permission];
    return config.roles.includes(userRole);
  };

  /**
   * Vérifie si l'utilisateur a au moins une des permissions données
   */
  const hasAnyPermission = (...permissions: HotelPermission[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  /**
   * Vérifie si l'utilisateur a toutes les permissions données
   */
  const hasAllPermissions = (...permissions: HotelPermission[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };

  /**
   * Vérifie si l'utilisateur a un des rôles donnés
   */
  const hasRole = (...roles: HotelUserRole[]): boolean => {
    return roles.includes(userRole);
  };

  /**
   * Vérifie si l'utilisateur est admin de l'hôtel
   */
  const isHotelAdmin = userRole === 'hotel_admin';

  /**
   * Vérifie si l'utilisateur est admin ou manager
   */
  const isAdminOrManager = userRole === 'hotel_admin' || userRole === 'hotel_manager';
  const isSuperAdmin = userRole === 'super_admin';

  return {
    user,
    userRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isHotelAdmin,
    isAdminOrManager,
    isSuperAdmin,
  };
}
